import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import { useState, useEffect } from 'react';
import ComicCard from '@/components/base/ComicCard';

function formatViews(v: number): string {
  if (v >= 10000) return (v / 10000).toFixed(1) + '万';
  return v.toString();
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedChapter, setSelectedChapter] = useState<number>(0);
  const [showAllChapters, setShowAllChapters] = useState(false);

  const comic = comics.find(c => c.slug === slug);

  useEffect(() => {
    setSelectedChapter(0);
    setShowAllChapters(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!comic) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
            <i className="ri-file-unknow-line text-3xl text-secondary-500"></i>
          </div>
          <h2 className="text-xl font-heading text-foreground-950 mb-2">漫画未找到</h2>
          <p className="text-sm text-foreground-600 mb-4">您要查看的漫画不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const chapter = comic.chapters[selectedChapter];
  const relatedComics = comics
    .filter(c => c.id !== comic.id && c.categorySlug === comic.categorySlug)
    .slice(0, 5);

  const displayedChapters = showAllChapters
    ? comic.chapters
    : comic.chapters.slice(0, 10);

  return (
    <div className="min-h-screen">
      <article>
        <section className="py-8 md:py-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={comic.title + ' - 韩漫免费在线阅读'}
                  className="w-40 md:w-48 h-auto rounded-lg object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 text-xs bg-primary-500 text-white rounded-full whitespace-nowrap">
                    {comic.status}
                  </span>
                  <Link
                    to={'/category/' + comic.categorySlug}
                    className="px-2 py-0.5 text-xs bg-accent-500 text-white rounded-full whitespace-nowrap hover:bg-accent-600 transition-colors"
                  >
                    {comic.category}
                  </Link>
                  {comic.tags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-xl md:text-2xl font-heading text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-4">
                  作者：<strong>{comic.author}</strong>
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-5 max-w-2xl">
                  {comic.description}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-6">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="font-semibold text-foreground-900">{comic.rating}</span>
                    <span>分</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    <span>{formatViews(comic.views)} 次阅读</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-file-list-3-line"></i>
                    <span>共 {comic.chapters.length} 话</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    <span>更新于 {comic.updatedAt}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => {
                      const el = document.getElementById('chapter-reader');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line mr-1"></i>开始阅读
                  </button>
                  {selectedChapter > 0 && (
                    <button
                      onClick={() => setSelectedChapter(prev => Math.max(0, prev - 1))}
                      className="px-4 py-2.5 bg-secondary-500 text-white text-sm font-medium rounded-full hover:bg-secondary-600 transition-colors duration-200 whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-1"></i>上一话
                    </button>
                  )}
                  {selectedChapter < comic.chapters.length - 1 && (
                    <button
                      onClick={() => setSelectedChapter(prev => Math.min(comic.chapters.length - 1, prev + 1))}
                      className="px-4 py-2.5 bg-secondary-500 text-white text-sm font-medium rounded-full hover:bg-secondary-600 transition-colors duration-200 whitespace-nowrap"
                    >
                      下一话<i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-72 flex-shrink-0 order-2 lg:order-1">
                <div className="sticky top-20">
                  <div className="bg-background-100 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-3">
                      章节目录 ({comic.chapters.length}话)
                    </h3>
                    <div className="space-y-1 max-h-[500px] overflow-y-auto">
                      {displayedChapters.map((ch, idx) => (
                        <button
                          key={ch.id}
                          onClick={() => {
                            setSelectedChapter(idx);
                            const el = document.getElementById('chapter-reader');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className={
                            'w-full text-left px-3 py-2 rounded text-sm transition-colors duration-150 ' +
                            (selectedChapter === idx
                              ? 'bg-primary-500 text-white'
                              : 'text-foreground-700 hover:bg-background-200')
                          }
                        >
                          <span className="text-xs opacity-70">第{ch.number}话</span>
                          <span className="block truncate">{ch.title}</span>
                        </button>
                      ))}
                    </div>
                    {comic.chapters.length > 10 && !showAllChapters && (
                      <button
                        onClick={() => setShowAllChapters(true)}
                        className="w-full mt-2 py-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 font-medium"
                      >
                        展开全部 {comic.chapters.length} 话 <i className="ri-arrow-down-s-line"></i>
                      </button>
                    )}
                    {showAllChapters && (
                      <button
                        onClick={() => setShowAllChapters(false)}
                        className="w-full mt-2 py-2 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 font-medium"
                      >
                        收起 <i className="ri-arrow-up-s-line"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0 order-1 lg:order-2" id="chapter-reader">
                <div className="bg-background-100 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground-950">
                      第{chapter.number}话：{chapter.title}
                    </h3>
                    <span className="text-xs text-foreground-500">更新于 {chapter.updatedAt}</span>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6 md:p-10">
                  <div className="max-w-[640px] mx-auto space-y-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(pageNum => (
                      <div
                        key={pageNum}
                        className="w-full aspect-[3/4] bg-background-200 rounded-lg overflow-hidden"
                      >
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center p-8">
                            <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-200">
                              <i className="ri-image-line text-2xl text-secondary-500"></i>
                            </div>
                            <p className="text-sm text-foreground-500 mb-1">漫画内容第 {pageNum} 页</p>
                            <p className="text-xs text-foreground-400">
                              第{chapter.number}话：{chapter.title}
                            </p>
                            <p className="text-xs text-foreground-400 mt-1">
                              {comic.title} - 韩漫免费下拉式阅读
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-6 px-4">
                  <button
                    onClick={() => setSelectedChapter(prev => Math.max(0, prev - 1))}
                    disabled={selectedChapter === 0}
                    className={
                      'px-5 py-2.5 text-sm font-medium rounded-full transition-colors duration-200 whitespace-nowrap ' +
                      (selectedChapter === 0
                        ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                        : 'bg-secondary-500 text-white hover:bg-secondary-600')
                    }
                  >
                    <i className="ri-arrow-left-line mr-1"></i>上一话
                  </button>
                  <span className="text-sm text-foreground-600">
                    {selectedChapter + 1} / {comic.chapters.length}
                  </span>
                  <button
                    onClick={() => setSelectedChapter(prev => Math.min(comic.chapters.length - 1, prev + 1))}
                    disabled={selectedChapter === comic.chapters.length - 1}
                    className={
                      'px-5 py-2.5 text-sm font-medium rounded-full transition-colors duration-200 whitespace-nowrap ' +
                      (selectedChapter === comic.chapters.length - 1
                        ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed'
                        : 'bg-primary-500 text-white hover:bg-primary-600')
                    }
                  >
                    下一话<i className="ri-arrow-right-line ml-1"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </article>

      {relatedComics.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h3 className="text-lg font-heading text-foreground-950 mb-6">
              更多{comic.category}漫画推荐
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {relatedComics.map(c => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}